import { useDeferredValue, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  checklistMaterialInputSchema,
  type ChecklistMaterial,
} from "cadendar-shared";
import { orderingApi, type CatalogOffer } from "../ordering/orderingApi";
import { trpc } from "../main/components/trpc";

export function uniqueCatalogProducts(offers: CatalogOffer[]) {
  return [...new Map(offers.map((offer) => [offer.productId, offer])).values()];
}

export function MaterialPicker({
  materials,
  selectedIds,
  disabled,
  onAdd,
}: {
  materials: ChecklistMaterial[];
  selectedIds: string[];
  disabled: boolean;
  onAdd: (id: string) => void;
}) {
  const utils = trpc.useUtils();
  const [filter, setFilter] = useState("");
  const [label, setLabel] = useState("");
  const [catalogSearch, setCatalogSearch] = useState("");
  const [product, setProduct] = useState<CatalogOffer | null>(null);
  const [error, setError] = useState("");
  const query = useDeferredValue(catalogSearch.trim());
  const catalog = useQuery({
    queryKey: ["ordering-catalog", query],
    queryFn: () => orderingApi.searchCatalog(query),
    enabled: query.length >= 3,
  });
  const save = trpc.checklist.saveMaterial.useMutation({
    onSuccess: async (saved) => {
      await utils.checklist.invalidate();
      onAdd(saved.id);
      setLabel("");
      setProduct(null);
      setCatalogSearch("");
      setError("");
    },
  });
  const busy = disabled || save.isPending;
  const available = materials.filter(
    (material) =>
      !selectedIds.includes(material.id) &&
      [material.label, material.catalogReference ?? ""]
        .join(" ")
        .toLocaleLowerCase("fr")
        .includes(filter.toLocaleLowerCase("fr")),
  );

  return (
    <div className="space-y-4 rounded border border-slate-200 bg-slate-50 p-4">
      <h3 className="font-semibold">Ajouter du matériel</h3>
      <label className="block">
        Matériel du cabinet
        <input
          className="mt-1 w-full rounded border bg-white p-2"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder="Rechercher dans le matériel réutilisable"
        />
      </label>
      <ul className="max-h-48 overflow-auto" aria-label="Matériel réutilisable">
        {available.map((material) => (
          <li
            key={material.id}
            className="flex items-center justify-between gap-2 border-b py-2"
          >
            <span>
              {material.label}
              {material.catalogReference && (
                <small className="ml-2 text-slate-500">
                  Réf. {material.catalogReference}
                </small>
              )}
            </span>
            <button
              type="button"
              className="rounded border bg-white px-3 py-1 disabled:opacity-50"
              disabled={busy}
              onClick={() => onAdd(material.id)}
            >
              Ajouter
            </button>
          </li>
        ))}
        {!available.length && (
          <li className="py-2 text-sm text-slate-500">
            Aucun autre matériel correspondant.
          </li>
        )}
      </ul>
      <details>
        <summary className="cursor-pointer font-medium">
          Créer un matériel libre ou depuis les commandes
        </summary>
        <div className="mt-3 space-y-3">
          <label className="block">
            Rechercher dans le catalogue de commandes
            <input
              className="mt-1 w-full rounded border bg-white p-2"
              value={catalogSearch}
              disabled={busy}
              onChange={(event) => setCatalogSearch(event.target.value)}
              placeholder="Nom, marque ou référence (3 caractères minimum)"
            />
          </label>
          {query.length >= 3 && catalog.isFetching && (
            <p role="status">Recherche…</p>
          )}
          {query.length >= 3 && catalog.error && (
            <p role="alert" className="text-red-700">
              {catalog.error.message}
            </p>
          )}
          {query.length >= 3 && catalog.data && (
            <ul
              className="max-h-60 overflow-auto"
              aria-label="Résultats du catalogue"
            >
              {uniqueCatalogProducts(catalog.data).map((offer) => (
                <li key={offer.productId}>
                  <button
                    type="button"
                    disabled={busy}
                    className="w-full border-b p-2 text-left hover:bg-white disabled:opacity-50"
                    onClick={() => {
                      const existing = materials.find(
                        (material) =>
                          material.catalogProductId === offer.productId,
                      );
                      if (existing) {
                        onAdd(existing.id);
                        return;
                      }
                      setProduct(offer);
                      setLabel(
                        [offer.brand, offer.commercialName]
                          .filter(Boolean)
                          .join(" · "),
                      );
                    }}
                  >
                    {offer.brand} · {offer.commercialName}
                    <small className="block text-slate-500">
                      {offer.manufacturerReference
                        ? `Réf. fabricant : ${offer.manufacturerReference}`
                        : `Réf. ${offer.supplierName} : ${offer.supplierReference}`}
                    </small>
                  </button>
                </li>
              ))}
              {!catalog.data.length && <li>Aucune référence trouvée.</li>}
            </ul>
          )}
          {product && (
            <p className="text-sm">
              Référence sélectionnée : {product.commercialName}{" "}
              <button
                type="button"
                className="underline"
                disabled={busy}
                onClick={() => setProduct(null)}
              >
                Dissocier
              </button>
            </p>
          )}
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              const parsed = checklistMaterialInputSchema.safeParse({
                label,
                catalogProductId: product?.productId ?? null,
              });
              if (!parsed.success) {
                setError(
                  parsed.error.issues[0]?.message ?? "Matériel invalide.",
                );
                return;
              }
              setError("");
              save.mutate(parsed.data);
            }}
          >
            <label className="min-w-48 flex-1">
              Libellé dans les checklists
              <input
                className="mt-1 w-full rounded border bg-white p-2"
                maxLength={300}
                required
                value={label}
                disabled={busy}
                onChange={(event) => setLabel(event.target.value)}
                placeholder="Ex. Plateau d’examen"
              />
            </label>
            <button
              className="rounded bg-blue-600 px-3 py-2 text-white disabled:opacity-50"
              disabled={busy}
            >
              {save.isPending ? "Création…" : "Créer et ajouter"}
            </button>
          </form>
          {(error || save.error) && (
            <p role="alert" className="text-red-700">
              {error || save.error?.message}
            </p>
          )}
        </div>
      </details>
    </div>
  );
}
